import React from 'react';
import { Image as ExpoImage } from 'expo-image';
import { Platform, StyleSheet, type ImageStyle, type StyleProp } from 'react-native';

import { WEB_IMAGE_ALT_DECORATIVE } from '../../helpers/imageWebAlt.helper';

function rnTransformToCss(
  transform: ImageStyle['transform'],
): string | undefined {
  if (!transform || !Array.isArray(transform)) return undefined;

  const parts: string[] = [];
  for (const entry of transform) {
    if (!entry || typeof entry !== 'object') continue;
    if ('translateY' in entry && entry.translateY != null) {
      parts.push(`translateY(${entry.translateY}px)`);
    }
    if ('translateX' in entry && entry.translateX != null) {
      parts.push(`translateX(${entry.translateX}px)`);
    }
    if ('scale' in entry && entry.scale != null) {
      parts.push(`scale(${entry.scale})`);
    }
  }

  return parts.length > 0 ? parts.join(' ') : undefined;
}

interface CdnImageProps {
  uri: string;
  style?: StyleProp<ImageStyle>;
  contentFit?: 'contain' | 'cover' | 'fill';
  testID?: string;
  alt?: string;
}

function isStorybookWebRuntime(): boolean {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return false;
  return Boolean(
    (window as Window & { __STORYBOOK_PREVIEW__?: unknown }).__STORYBOOK_PREVIEW__,
  );
}

/**
 * CDN image for web + native. Storybook's expo-image mock cannot load remote URIs via
 * RN Image, so we render a real HTML img in that environment only.
 */
export function CdnImage({
  uri,
  style,
  contentFit = 'contain',
  testID,
  alt = WEB_IMAGE_ALT_DECORATIVE,
}: CdnImageProps): React.ReactElement {
  if (isStorybookWebRuntime()) {
    const flatStyle = StyleSheet.flatten(style) ?? {};
    const { transform: rnTransform, ...restStyle } = flatStyle;
    const cssTransform = rnTransformToCss(rnTransform);
    const objectFit =
      contentFit === 'contain' ? 'contain' : contentFit === 'fill' ? 'fill' : 'cover';
    /** RN DimensionValue includes null/AnimatedNode; CSS width/height do not. */
    const toCssDimension = (value: unknown): number | string | undefined =>
      typeof value === 'number' || typeof value === 'string' ? value : undefined;
    const width = toCssDimension(restStyle.width);
    const height = toCssDimension(restStyle.height);

    return (
      <img
        src={uri}
        alt={alt}
        data-testid={testID}
        style={{
          ...(restStyle as React.CSSProperties),
          display: 'block',
          width,
          objectFit,
          ...(cssTransform ? { transform: cssTransform } : {}),
          /** Fixed height matches RN layout box — auto height broke monkey/card overlap in Storybook. */
          ...(height != null ? { height } : {}),
        }}
      />
    );
  }

  return (
    <ExpoImage
      source={{ uri }}
      style={style}
      contentFit={contentFit}
      alt={alt}
      accessibilityLabel={alt}
      testID={testID}
    />
  );
}
